import axios from 'axios'

// The attorney portal runs on its own subdomain (attorneys.coloradoleasecheck.com)
// and its own build. Its own axios instance so the renter app's redirect
// interceptors (402 -> /legal etc.) never apply here. Token is per-origin
// in localStorage, isolated from the renter app.
const TOKEN_KEY = 'attorney_token'

const api = axios.create({ baseURL: '/api' })

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      localStorage.removeItem(TOKEN_KEY)
      window.location.assign('/login')
    }
    const detail = error.response?.data?.detail
    error.message = typeof detail === 'string' ? detail : error.message
    return Promise.reject(error)
  },
)

export const attorneyAuth = {
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
  logout: () => localStorage.removeItem(TOKEN_KEY),
  // Login reuses the shared /auth/login (attorneys are Users with role=attorney).
  login: async (email, password) => {
    const body = new URLSearchParams()
    body.set('username', email)
    body.set('password', password)
    const r = await api.post('/v1/auth/login', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    localStorage.setItem(TOKEN_KEY, r.data.access_token)
    return r.data
  },
  register: (payload) => api.post('/v1/attorney/register', payload).then((r) => r.data),
}

export const attorneyApi = {
  me: () => api.get('/v1/attorney/me').then((r) => r.data),
  updateProfile: (payload) => api.patch('/v1/attorney/me', payload).then((r) => r.data),
  uploadPhoto: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/v1/attorney/me/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data)
  },
  plans: () => api.get('/v1/attorney/plans').then((r) => r.data),
  checkout: (tier) =>
    api.post('/v1/attorney/subscription/checkout', { tier }).then((r) => r.data),
  boardSeats: () => api.get('/v1/attorney/review-board/seats').then((r) => r.data),
  applyBoard: () => api.post('/v1/attorney/review-board/apply').then((r) => r.data),
  sendConfirmationEmail: () => api.post('/v1/attorney/confirmation-email').then((r) => r.data),
  // Verify-by-email (admin, token-authorized — no login).
  verifyLookup: (token) => api.get(`/v1/attorney/verify/${token}`).then((r) => r.data),
  verifyDecide: (token, action) =>
    api.post(`/v1/attorney/verify/${token}`, { action }).then((r) => r.data),
  // Review Board decision by email link (admin, token-authorized — no login).
  boardReviewLookup: (token) => api.get(`/v1/attorney/board-review/${token}`).then((r) => r.data),
  boardReviewDecide: (token, action) =>
    api.post(`/v1/attorney/board-review/${token}`, { action }).then((r) => r.data),
  // Free Founding Membership (no Stripe): apply + admin decision by email link.
  foundingApply: () => api.post('/v1/attorney/founding/apply').then((r) => r.data),
  foundingReviewLookup: (token) => api.get(`/v1/attorney/founding-review/${token}`).then((r) => r.data),
  foundingReviewDecide: (token, action) =>
    api.post(`/v1/attorney/founding-review/${token}`, { action }).then((r) => r.data),
}

export default api
